import {useConfig} from '@aboutyou/config';
import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React, {SyntheticEvent, useCallback, useState} from 'react';
import {COLORS} from '../../../constants/colors';
import {UnstyledButton} from '../../atoms/ButtonsDeprecated/UnstyledButton';
import {ContentPopper} from '../../atoms/ContentPopper/ContentPopper';
import {SecondaryContentPopperButton} from '../../atoms/ContentPopper/ContentPopperButtons/ContentPopperButtons';
import {Flyout} from '../../atoms/Flyout/Flyout';
import {Icon, IconSize} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {ModalDialog} from '../../atoms/ModalDialog/ModalDialog';
import {tabFocusedStyles} from '../../atoms/TabFocused/tabFocusedStyles';
import {
  TermsAndConditionsBody,
  TermsAndConditionsHeader,
} from '../../atoms/TermsAndConditions/TermsAndConditions';
import {CountdownBoxed} from '../../molecules/Countdown/CountdownBoxed';

interface InfoCircleWithDialogProps {
  header?: string;
  conditions?: string;
  endsAt?: Date;
  countdownBackgroundColor?: string;
  position?: 'right' | 'left';
  infoIconVariant?: 'filled' | 'hollow';
  toggleClick?: (state: boolean) => void;
  className?: string;
  style?: React.CSSProperties;
  dataTestid?: string;
  fill?: string;
  iconSize?: keyof typeof IconSize;
  onOpenCallback?: VoidFunction;
  onCloseCallback?: VoidFunction;
}

export const InfoCircleWithDialog: React.FC<InfoCircleWithDialogProps> = ({
  header,
  conditions,
  className,
  endsAt,
  countdownBackgroundColor,
  toggleClick,
  infoIconVariant = 'filled',
  position = 'right',
  dataTestid = 'infoCircleWithDialog',
  fill = COLORS['Fill.White'],
  iconSize,
  style,
  onCloseCallback,
  onOpenCallback,
}) => {
  const {device} = useConfig();
  const [showModal, setShowModal] = useState(false);
  const {t} = useTranslation();

  const open = useCallback((e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenCallback?.();
    setShowModal(true);
    toggleClick?.(true);
  }, []);

  const close = useCallback((e?: React.MouseEvent<Element, MouseEvent>) => {
    e?.preventDefault();
    e?.stopPropagation();
    onCloseCallback?.();
    setShowModal(false);
    toggleClick?.(false);
  }, []);

  const assets =
    infoIconVariant === 'filled'
      ? GenericIconName.InfoFilled
      : GenericIconName.Info;

  return (
    <>
      <ContentPopper
        LayoutComponent={device === 'desktop' ? ModalDialog : Flyout}
        open={showModal}
        onClose={close}
        renderInPortal
        aboveHeaderElement={
          device === 'mobile' &&
          endsAt && (
            <StyledCountdownBoxed
              endDate={endsAt}
              size="small"
              backgroundColor={countdownBackgroundColor}
            />
          )
        }
        headerContent={
          <TermsAndConditionsHeader>{header}</TermsAndConditionsHeader>
        }
        bodyContent={
          <TermsAndConditionsBody>{conditions}</TermsAndConditionsBody>
        }
        footerContent={
          <SecondaryContentPopperButton label={t('Close')} onClick={close} />
        }
      />

      <InfoIconButton
        aria-label={t('Open info window')}
        className={className}
        data-position={position}
        onClick={open}
        data-testid={dataTestid}
        style={style}
      >
        <Icon
          asset={
            infoIconVariant === 'filled' && fill
              ? GenericIconName.InfoWithoutFill
              : assets
          }
          size={iconSize || 'XS'}
          fill={fill}
        />
      </InfoIconButton>
    </>
  );
};

const StyledCountdownBoxed = styled(CountdownBoxed)`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 6px 0 12px 0;
  border-radius: 16px 16px 0 0;
  z-index: 0;
  margin-bottom: -6px;
`;

const InfoIconButton = styled(UnstyledButton)`
  position: absolute;
  top: 0.625rem;
  left: 0.625rem;

  &[data-position='right'] {
    top: 0.313rem;
    right: 0.313rem;
    left: unset;
  }

  &:focus-visible {
    ${tabFocusedStyles()}
  }
`;
